import React, { useCallback, useState } from "react"
import PropTypes from "prop-types"
import { Flex } from "antd"
import { useDispatch } from "react-redux"
import { useTranslation } from "react-i18next"
import { NewConfirmModal } from "ui/NewConfirmModal"
import { ConfirmModal } from "ui/ConfirmModal"
import BookingConflictModal from "components/common/BookingConflictModal"
import BlackoutFormModal from "pagesV2/pro/Settings/Blackout/modals/BlackoutFormModal"
import { useBlackouts } from "pagesV2/pro/Settings/Blackout/hooks/useBlackouts"
import { ROW_MENU_ACTIONS } from "pagesV2/pro/Settings/Blackout/constants"
import BlackoutDayCard from "pagesV2/pro/Calendar/DayDetailsPanel/BlackoutDayCard"
import ScheduleTimeOffModal from "pages/guide/resources/crewMembers/crewDetail/components/ScheduledTimeOff/modal/ScheduleTimeOff"
import { BLACKOUT_SCOPES, isSameId } from "helpers/blackouts"
import { getBlackout } from "store/blackouts"
import { usePermissions } from "hooks/usePermissions"
import { REASON_TYPES } from "../../constants"
import { useCrewOffActions } from "../../hooks/useCrewOffActions"
import styles from "./index.module.css"
import DeleteBlackoutMessage from "ui/Blackout/DeleteBlackoutMessage"

/**
 * A day's blackout reason in the shape `BlackoutDayCard` reads.
 *
 * The availability feed states everything the card shows on the reason itself — the dates of the
 * entry covering this day, the guide's note, the stranded resources and listings, the booking count
 * — so the card no longer depends on finding the record in another list. That lookup is what left a
 * card reading "0 items unavailable + 0 listings affected" whenever the record was missing from it.
 *
 * A crew off is a crew member's time off: its id is a time-off id, and its actions need the crew
 * member's profile id too, which is the reason's one resource.
 */
const toDayCard = (reason, date) => {
  const isTimeOff = reason.type === REASON_TYPES.CREW_OFF
  const scope = reason.scope || (isTimeOff ? BLACKOUT_SCOPES.CREW : undefined)
  const resources = reason.resources || []

  return {
    id: reason.blackout_id,
    scope,
    label: reason.label,
    // The guide's note. `detail` repeats the label, so it is not a stand-in.
    reason: reason.reason || "",
    recurring_yearly: Boolean(reason.recurring_yearly),
    start_date: reason.start_date || date,
    end_date: reason.end_date || reason.start_date || date,
    start_time: reason.start_time ?? null,
    end_time: reason.end_time ?? null,
    all_listings: scope === BLACKOUT_SCOPES.BUSINESS,
    listings: reason.listings || [],
    resources,
    bookings_requiring_action: reason.conflicting_bookings_count ?? 0,
    isTimeOff,
    profileId: isTimeOff ? reason.profile_id ?? resources[0]?.id ?? null : null
  }
}

/**
 * The blackouts that make this listing unsellable on this day, one card per reason.
 *
 * The edit form is the one thing the reason cannot fill: it needs every date entry and the targets,
 * so Edit fetches that single record (`GET /guide/blackouts/:id`) when it is clicked rather than the
 * drawer loading the whole blackout list up front.
 */
const BlackoutSection = ({ reasons, date, onReview, onChanged }) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [isFetchingRecord, setIsFetchingRecord] = useState(false)

  const {
    isSaving,
    isDeleting,
    formState,
    openEditModal,
    closeFormModal,
    handleSubmit,
    blackoutToDelete,
    setBlackoutToDelete,
    handleDelete
  } = useBlackouts({ enabled: false })

  const crewOff = useCrewOffActions({ onChanged })
  const { hasFullAccessFlag, activeProfile } = usePermissions()
  // Crew and staff change only their own time off; the guide/admin uses blackouts
  const isOwnTimeOff = (card) => !hasFullAccessFlag && isSameId(card.profileId, activeProfile?.id)

  const cards = reasons.map((reason) => toDayCard(reason, date))

  const editBlackout = useCallback(
    (card) => {
      if (isFetchingRecord) return
      setIsFetchingRecord(true)
      dispatch(getBlackout(card.id))
        .then((record) => record && openEditModal(record))
        // The api() helper has already surfaced the failure.
        .catch(() => {})
        .finally(() => setIsFetchingRecord(false))
    },
    [dispatch, isFetchingRecord, openEditModal]
  )

  const getBlackoutMenuItems = (card) => [
    {
      key: ROW_MENU_ACTIONS.EDIT,
      label: t("settings.action_edit", { defaultValue: "Edit" }),
      disabled: isFetchingRecord,
      onClick: () => editBlackout(card)
    },
    {
      key: ROW_MENU_ACTIONS.DELETE,
      label: t("settings.action_delete", { defaultValue: "Delete" }),
      danger: true,
      onClick: () => setBlackoutToDelete(card)
    }
  ]

  // A card without the ids its actions need gets no menu rather than one that would fail.
  const getMenuItems = (card) => {
    if (!card.id) return []
    if (card.isTimeOff) return card.profileId && isOwnTimeOff(card) ? crewOff.getMenuItems(card) : []
    // Blackouts are handled by the guide/admin only
    return hasFullAccessFlag ? getBlackoutMenuItems(card) : []
  }

  const submitAndRefresh = async (payload) => {
    await handleSubmit(payload)
    onChanged?.()
  }

  const deleteAndRefresh = async () => {
    await handleDelete()
    onChanged?.()
  }

  return (
    <Flex vertical gap="var(--spacing-s)">
      <span className={styles.sectionHeading}>
        {t("availability.blackouts_affecting", { defaultValue: "Blackouts affecting this experience" })}
      </span>

      {cards.map((card, index) => (
        <BlackoutDayCard
          // Time-off and blackout ids come from different tables, so the type is part of the key.
          key={`${card.isTimeOff ? "time-off" : "blackout"}-${card.id || index}`}
          blackout={card}
          date={date}
          menuItems={getMenuItems(card)}
          onReview={() => onReview?.(card)}
          onReplaced={onChanged}
          // The replacement endpoints take a blackout id; a time-off id has no route there yet.
          canReplace={!card.isTimeOff}
        />
      ))}

      {formState && (
        <BlackoutFormModal
          isOpen
          scope={formState.scope}
          blackout={formState.blackout}
          onCancel={closeFormModal}
          onSubmit={submitAndRefresh}
          isSaving={isSaving}
        />
      )}

      <NewConfirmModal
        visible={!!blackoutToDelete}
        onClose={() => setBlackoutToDelete(null)}
        onConfirm={deleteAndRefresh}
        title={t("settings.blackout.delete_title", { defaultValue: "Delete Blackout?" })}
        message={<DeleteBlackoutMessage blackout={blackoutToDelete} date={date} />}
        warningText={t("settings.blackout.delete_warning", { defaultValue: "All related restrictions will be removed immediately." })}
        hasIcon={false}
        confirmLabel={t("settings.action_delete", { defaultValue: "Delete" })}
        loading={isDeleting}
      />

      {crewOff.editing && (
        <ScheduleTimeOffModal
          open
          setIsModalOpen={(isOpen) => !isOpen && crewOff.closeEdit()}
          onCancel={crewOff.closeEdit}
          onSave={crewOff.saveEdit}
          editMode
          initialValues={crewOff.editing.values}
          saveLoading={crewOff.isSaving}
        />
      )}

      <ConfirmModal
        open={!!crewOff.cardToDelete}
        title={t("crewMember.scheduledTimeOff.deleteConfirmation.title")}
        info={t("crewMember.scheduledTimeOff.deleteConfirmation.message")}
        onCancel={crewOff.cancelDelete}
        onDelete={crewOff.confirmDelete}
        deleteLabel={t("crewMember.scheduledTimeOff.table.actions.delete")}
        cancelLabel={t("crewMember.scheduledTimeOff.buttons.cancel")}
        loading={crewOff.isDeleting}
      />

      <BookingConflictModal {...crewOff.bookingConflictModalProps} />
    </Flex>
  )
}

BlackoutSection.propTypes = {
  /** The day's `blackout` and `crew_off` reasons, each carrying what its card shows. */
  reasons: PropTypes.arrayOf(
    PropTypes.shape({
      type: PropTypes.string,
      blackout_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      scope: PropTypes.string,
      label: PropTypes.string,
      reason: PropTypes.string,
      start_date: PropTypes.string,
      end_date: PropTypes.string,
      start_time: PropTypes.string,
      end_time: PropTypes.string,
      recurring_yearly: PropTypes.bool,
      conflicting_bookings_count: PropTypes.number,
      resources: PropTypes.array,
      listings: PropTypes.array
    })
  ).isRequired,
  date: PropTypes.string.isRequired,
  /** Opens the reservations a blackout strands; called with that card. */
  onReview: PropTypes.func,
  /** Called after a blackout or time off is saved, deleted or replaced so the grid can refetch. */
  onChanged: PropTypes.func
}

export default BlackoutSection
